import KtvIcon from '@assets/icons/KtvIcon'
import Microsoft365Icon from '@assets/icons/Microsoft365Icon'
import SlackIcon from '@assets/icons/SlackIcon'
import SsoIcon from '@assets/icons/SsoIcon'
import { AppPermissionChecker } from '@auth/useApps'
import AdminPaths from '@globals/paths/admin'
import { colors } from '@globals/themes/defaultTheme'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import { SxProps } from '@mui/material/styles'
import { AppPermission } from '@shared/permissions'
import { T } from '@transifex/react'
import { ReactNode } from 'react'
import SlackIntegrationCardActions from './slack/SlackIntegrationCardActions'

export type IntegrationType = 'hris' | 'sso' | 'ftp' | 'ktv' | 'ms365' | 'slack'

export type IntegrationData = {
  key: IntegrationType
  icon: ReactNode
  title: ReactNode
  subtitle: ReactNode
  description: ReactNode
  visible: (hasAppPermission: AppPermissionChecker) => boolean
} & ({ to?: never; actions: ReactNode } | { to: string; actions?: never })

const iconStyles: SxProps = {
  '& *': {
    fill: colors.bluePrimary,
  },
}

export const integrations: IntegrationData[] = [
  {
    key: 'hris',
    icon: <GroupsOutlinedIcon sx={iconStyles} />,
    title: <T _str="HRIS" />,
    subtitle: <T _str="Human Resource Information System" />,
    description: (
      <T _str="Connect your HRIS to Kudos to automate user provisioning and keep employee records in sync" />
    ),
    to: AdminPaths.hrisIntegrations('manage'),
    visible: hasAppPermission =>
      hasAppPermission('hrIntegrationApp', AppPermission.HrIntegrationAppBrowse),
  },
  {
    key: 'sso',
    icon: <SsoIcon />,
    title: <T _str="SSO" />,
    subtitle: <T _str="Single sign-on" />,
    description: (
      <T _str="Simplify sign-in and improve security by enabling single sign-on with your organization’s identity provider" />
    ),
    to: AdminPaths.ssoIntegrations(),
    visible: hasAppPermission =>
      hasAppPermission('identityApp', AppPermission.IdentityAppManageSamlProviders),
  },
  {
    key: 'ftp',
    icon: <DriveFileMoveOutlinedIcon sx={iconStyles} />,
    title: <T _str="FTP" />,
    subtitle: <T _str="File transfer protocol" />,
    description: (
      <T _str="Securely transfer user data from your FTP server to Kudos, keeping your employee records accurate and up to date" />
    ),
    to: AdminPaths.ftpIntegrations('connections'),
    visible: hasAppPermission =>
      hasAppPermission('hrIntegrationApp', AppPermission.HrIntegrationAppBrowseFtpConnections),
  },
  {
    key: 'ktv',
    icon: <KtvIcon />,
    title: <T _str="Kudos TV" />,
    subtitle: null,
    description: (
      <T _str="Showcase real-time recognition on break room TVs, digital displays, or your intranet to keep appreciation front and centre" />
    ),
    to: AdminPaths.kudosTvIntegrations(),
    visible: hasAppPermission => hasAppPermission('tvApp', AppPermission.TvAppBrowse),
  },
  {
    key: 'ms365',
    icon: <Microsoft365Icon />,
    title: <T _str="Microsoft 365" />,
    subtitle: null,
    description: (
      <T _str="Make recognition part of your team’s daily workflow by enabling the Microsoft 365 integration" />
    ),
    to: AdminPaths.ms365Integrations(),
    visible: hasAppPermission =>
      hasAppPermission('identityApp', AppPermission.IdentityAppEditTenantPreferences),
  },
  {
    key: 'slack',
    icon: <SlackIcon />,
    title: <T _str="Slack" />,
    subtitle: null,
    description: (
      <T _str="Make recognition part of your team’s daily workflow by installing the Slack integration" />
    ),
    actions: <SlackIntegrationCardActions />,
    visible: hasAppPermission =>
      hasAppPermission('identityApp', AppPermission.IdentityAppCreateSlackIdentity),
  },
]

export const hasAnyIntegrationPermissions = (permissionChecker: AppPermissionChecker) =>
  integrations.some(integration => integration.visible(permissionChecker))
